// ─── src/components/dashboard/FilterBar.tsx ───────────────────────────────────
import { type FC, type ChangeEvent, useRef } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";

export interface Filters {
  search: string;
  gender: string;
  currentStatus: string;
  isPitchingIdea: string;   // "all" | "true" | "false"
  requestingScholarship: string;
  city: string;
  appStatus: string;        // "all" | "pending" | "accepted" | "rejected"
  primaryRole: string;
}

interface FilterBarProps {
  filters: Filters;
  onChange: (filters: Filters) => void;
  onReset: () => void;
  isMaster?: boolean;
}

const Select: FC<{
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}> = ({ label, value, options, onChange }) => (
  <div className="flex flex-col gap-1 min-w-[140px]">
    <label className="text-xs text-brand-muted font-medium">{label}</label>
    <select
      value={value}
      onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
      className="bg-brand-card border border-brand-border text-white text-sm
                 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-orange
                 transition-colors cursor-pointer"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} className="bg-brand-card">
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

const BOOL_OPTIONS = [
  { value: "all",   label: "All" },
  { value: "true",  label: "Yes" },
  { value: "false", label: "No" },
];

export const FilterBar: FC<FilterBarProps> = ({ filters, onChange, onReset, isMaster }) => {
  const searchRef = useRef<HTMLInputElement>(null);
  const isDirty = Object.entries(filters).some(
    ([k, v]) => k === "search" ? v !== "" : v !== "all"
  );

  const set = (key: keyof Filters) => (val: string) =>
    onChange({ ...filters, [key]: val });

  return (
    <div className="card p-4 flex flex-wrap items-end gap-4">
      {/* Search */}
      <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
        <label className="text-xs text-brand-muted font-medium">Search</label>
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted"
          />
          <input
            ref={searchRef}
            type="text"
            placeholder="Name, email, city…"
            value={filters.search}
            onChange={(e) => set("search")(e.target.value)}
            className="w-full bg-brand-card border border-brand-border text-white
                       text-sm rounded-lg pl-9 pr-4 py-2 placeholder:text-brand-muted
                       focus:outline-none focus:border-brand-orange transition-colors"
          />
        </div>
      </div>

      {/* Dropdowns */}
      <Select
        label="Gender"
        value={filters.gender}
        options={[
          { value: "all",    label: "All genders" },
          { value: "male",   label: "Male" },
          { value: "female", label: "Female" },
        ]}
        onChange={set("gender")}
      />

      <Select
        label="Occ. Status"
        value={filters.currentStatus}
        options={[
          { value: "all",          label: "All statuses" },
          { value: "Student",      label: "Student" },
          { value: "Employed",     label: "Employed" },
          { value: "Freelancer",   label: "Freelancer" },
          { value: "Founder",      label: "Founder" },
        ]}
        onChange={set("currentStatus")}
      />

      <Select
        label="App. Status"
        value={filters.appStatus}
        options={[
          { value: "all",      label: "All statuses" },
          { value: "pending",  label: "Pending" },
          { value: "accepted", label: "Accepted" },
          { value: "rejected", label: "Rejected" },
        ]}
        onChange={set("appStatus")}
      />

      <Select
        label="Role"
        value={filters.primaryRole}
        options={[
          { value: "all",              label: "All roles" },
          { value: "developer",        label: "Developer" },
          { value: "designer",         label: "Designer" },
          { value: "business",         label: "Business" },
          { value: "expert",           label: "Expert" },
          { value: "BusinessMarketing",label: "Biz. Marketing" },
          { value: "ProductOther",     label: "Product / Other" },
          { value: "Student",          label: "Student" },
        ]}
        onChange={set("primaryRole")}
      />

      {isMaster && (
        <Select
          label="City"
          value={filters.city}
          options={[
            { value: "all",         label: "All cities" },
            { value: "Damascus",    label: "Damascus" },
            { value: "Aleppo",      label: "Aleppo" },
            { value: "Lattakia",     label: "Lattakia" },
            { value: "Homs",        label: "Homs" },
            { value: "Hama",        label: "Hama" },
            { value: "Daraa",       label: "Daraa" },
            { value: "Idlib",       label: "Idlib" },
            { value: "Deir ez-Zur", label: "Deir ez-Zur" },
          ]}
          onChange={set("city")}
        />
      )}

      <Select
        label="Pitching Idea"
        value={filters.isPitchingIdea}
        options={BOOL_OPTIONS}
        onChange={set("isPitchingIdea")}
      />

      <Select
        label="Scholarship"
        value={filters.requestingScholarship}
        options={BOOL_OPTIONS}
        onChange={set("requestingScholarship")}
      />

      {/* Reset */}
      {isDirty && (
        <button
          onClick={onReset}
          className="flex items-center gap-1.5 btn-ghost text-sm self-end"
          aria-label="Reset filters"
        >
          <X size={14} />
          Reset
        </button>
      )}
    </div>
  );
};

